import { extractHairConcernScores } from './hairReportUtils';
import { buildInsightConditionScores } from './insightTreatmentScores';
import { getSessionId } from './sessionManager';
import { resolveHairSalonApiDeps } from './hairSalonApiDeps';
import { resolveHairSalonVisitSessionId } from './hairSalonFlowDeps';

export function normalizeRecommendGender(gender) {
  const g = String(gender || '').trim().toLowerCase();
  if (g === 'male' || g === 'm' || g === 'men' || g === 'man') return 'male';
  if (g === 'female' || g === 'f' || g === 'women' || g === 'woman') return 'female';
  return undefined;
}

export async function fetchHairTreatmentRecommendations(hairResults, opts = {}) {
  const {
    getApiBaseUrl,
    SALON_API_PREFIX,
    fetchWithRetry,
    TIMEOUT_MS,
    franchiseAuthHeaders,
    resolveFranchiseScopeIds,
  } = resolveHairSalonApiDeps();
  const base = (getApiBaseUrl() || '').replace(/\/$/, '');
  if (!base) throw new Error('Salon API is not configured');

  const { condition_scores, detected_conditions } = extractHairConcernScores(hairResults);
  const scope = resolveFranchiseScopeIds();
  const orgId = opts.organizationId || scope.organizationId;
  const franchiseId = opts.franchiseId || scope.franchiseId;
  const salonSessionId = opts.sessionId || resolveHairSalonVisitSessionId() || getSessionId();

  const res = await fetchWithRetry(
    `${base}${SALON_API_PREFIX}/recommend-hair-treatments`,
    {
      method: 'POST',
      credentials: 'include',
      headers: franchiseAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        session_id: salonSessionId || undefined,
        hair_insight_session_id: opts.hairInsightSessionId || undefined,
        hair_results: hairResults,
        condition_scores,
        detected_conditions,
        organization_id: orgId || undefined,
        franchise_id: franchiseId || undefined,
        app_session_token: opts.token || undefined,
        treatment_suggest_limit: opts.limit ?? 6,
        gender: opts.gender || undefined,
      }),
      timeoutMs: TIMEOUT_MS.SAVE_ANALYSIS,
    },
    { label: 'recommend-hair-treatments' },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || data?.message || 'Could not load recommended services');
  }
  return data;
}

/** Rank outlet price-list services from a Hair Insight dashboard payload. */
export async function fetchInsightTreatmentRecommendations(dashboard, opts = {}) {
  const {
    getApiBaseUrl,
    SALON_API_PREFIX,
    fetchWithRetry,
    TIMEOUT_MS,
    franchiseAuthHeaders,
    resolveFranchiseScopeIds,
  } = resolveHairSalonApiDeps();
  const base = (getApiBaseUrl() || '').replace(/\/$/, '');
  if (!base) throw new Error('Salon API is not configured');

  const { condition_scores, detected_conditions } = buildInsightConditionScores(dashboard || {});
  const scope = resolveFranchiseScopeIds();
  const orgId = opts.organizationId || scope.organizationId;
  const franchiseId = opts.franchiseId || scope.franchiseId;
  const salonSessionId = opts.sessionId || resolveHairSalonVisitSessionId() || getSessionId();
  if (!orgId || !franchiseId) {
    throw new Error('Outlet login is required to load personalized services (organization / franchise).');
  }

  const res = await fetchWithRetry(
    `${base}${SALON_API_PREFIX}/recommend-hair-treatments`,
    {
      method: 'POST',
      credentials: 'include',
      headers: franchiseAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        session_id: salonSessionId || undefined,
        hair_insight_session_id: dashboard?.sessionId || opts.hairInsightSessionId || undefined,
        hair_results: {},
        condition_scores,
        detected_conditions,
        organization_id: orgId || undefined,
        franchise_id: franchiseId || undefined,
        app_session_token: opts.token || undefined,
        treatment_suggest_limit: opts.limit ?? 6,
        gender: opts.gender || undefined,
      }),
      timeoutMs: TIMEOUT_MS.SAVE_ANALYSIS,
    },
    { label: 'insight-recommend-hair-treatments' },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || data?.message || 'Could not load recommended services');
  }
  return data;
}

/** Rank + persist hair-domain franchise products for front desk / check-in. */
export async function fetchInsightProductRecommendations(dashboard, opts = {}) {
  const {
    getApiBaseUrl,
    SALON_API_PREFIX,
    fetchWithRetry,
    TIMEOUT_MS,
    franchiseAuthHeaders,
    resolveFranchiseScopeIds,
  } = resolveHairSalonApiDeps();
  const base = (getApiBaseUrl() || '').replace(/\/$/, '');
  if (!base) throw new Error('Salon API is not configured');

  const salonSessionId = opts.sessionId || resolveHairSalonVisitSessionId() || getSessionId();
  if (!salonSessionId) {
    throw new Error('Salon session is required to save hair product recommendations');
  }

  const { condition_scores, detected_conditions } = buildInsightConditionScores(dashboard || {});
  const scope = resolveFranchiseScopeIds();
  const orgId = opts.organizationId || scope.organizationId;
  const franchiseId = opts.franchiseId || scope.franchiseId;

  const res = await fetchWithRetry(
    `${base}${SALON_API_PREFIX}/recommend-hair-products`,
    {
      method: 'POST',
      credentials: 'include',
      headers: franchiseAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        session_id: salonSessionId,
        hair_results: {},
        condition_scores,
        detected_conditions,
        organization_id: orgId || undefined,
        franchise_id: franchiseId || undefined,
        app_session_token: opts.token || undefined,
        product_suggest_limit: opts.limit ?? 8,
        gender: opts.gender || undefined,
        product_domain: 'hair',
        final_skin_score: 55,
        skin_grade: 'Transitioning',
      }),
      timeoutMs: TIMEOUT_MS.SAVE_ANALYSIS,
    },
    { label: 'insight-recommend-hair-products' },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || data?.message || 'Could not load recommended products');
  }
  return data;
}
